/**
 * EvidenceRefs — the identifiers Merlin read for one fact or one next step
 * (kanban 1af16b1c; Scott/Jess 2026-10-01: "for each of them you could
 * click them to see the details of what it checked"). Rendered only inside
 * something the reader opened — a tile's pop-out, a step's "Why" — never on
 * the page by default (Scott, 2026-10-06: always-visible hints are noise).
 *
 * Each ref is a chip (kind + the number as filed) with Merlin's note on what
 * it showed; a ref that names one of the thread's loaded signals also quotes
 * that signal's observation, so the evidence is the thing itself, not a
 * pointer to it.
 */
import { EVIDENCE_KIND_LABEL, signalForRef, type EvidenceRef } from './merlinBrief';
import styles from './EvidenceRefs.module.css';

export function EvidenceRefs({
  refs,
  signals = [],
  label = 'Read for it',
}: {
  refs: EvidenceRef[];
  /** The thread's loaded signals, so a signal ref can quote its observation. */
  signals?: ReadonlyArray<{ id: string; observation: string }>;
  label?: string;
}) {
  if (refs.length === 0) return null;
  return (
    <div className={styles.wrap}>
      <span className={styles.label}>{label}</span>
      <ul className={styles.list} aria-label="What Merlin read">
        {refs.map((r) => {
          const kind = EVIDENCE_KIND_LABEL[r.kind] ?? '';
          const signal = signalForRef(r, signals);
          return (
            <li key={`${r.kind}:${r.ref}`} className={styles.item} data-kind={r.kind || 'other'}>
              <span className={styles.chip}>
                {kind && <strong>{kind}</strong>}
                {signal ? 'signal' : r.ref}
              </span>
              {r.note && <span className={styles.note}>{r.note}</span>}
              {signal && <q className={styles.signal}>{signal.observation}</q>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default EvidenceRefs;
